import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  } from 'react-native';
import { useRouter } from 'expo-router';
import KeyboardAwareView from '../../../src/components/KeyboardAwareView';
import AiSuggestionsCard from '../../../src/components/AiSuggestionsCard';
import AiUnavailableBanner from '../../../src/components/AiUnavailableBanner';
import { showAlert } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useOnDeviceLlm } from '../../../src/hooks/useOnDeviceLlm';
import { visionsApi } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import type { SuggestionItem } from '../../../src/services/llm';
import { encrypt, encryptTitle, mergeEncryptionMetadata, getKeyForTeam } from '../../../src/utils/encryption';
import { useTeamStore } from '../../../src/stores/teamStore';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

export default function CreateVisionScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useThemedStyles(createStyles);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [futureLetter, setFutureLetter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<SuggestionItem[]>([]);
  const [showAiResults, setShowAiResults] = useState(false);
  const llm = useOnDeviceLlm();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  const handleAiSuggest = async () => {
    if (!title.trim()) {
      showAlert('Add a Title', 'Enter a vision title first so AI can help refine it.');
      return;
    }
    setShowAiResults(true);
    setAiSuggestions([]);
    const result = await llm.suggest('vision', `${title.trim()}: ${description.trim()}`);
    if (result) {
      setAiSuggestions(result.suggestions);
    }
  };

  const handleSubmit = async () => {
    if (!requireConnectivity()) return;
    if (!title.trim()) {
      showAlert('Required', 'Please enter a vision title.');
      return;
    }

    setIsSubmitting(true);
    try {
      const key = await getKeyForTeam(activeTeamId);
      const { title_encrypted, title_iv } = await encryptTitle(title.trim(), key);
      let descEncrypted: string | undefined;
      let descIv: string | undefined;
      if (description.trim()) {
        const descResult = await encrypt(description.trim(), key);
        descEncrypted = descResult.encrypted;
        descIv = descResult.metadata.iv;
      }
      let futureLetterEncrypted: string | undefined;
      const additionalIvs: Record<string, string> = {};
      if (futureLetter.trim()) {
        const letterResult = await encrypt(futureLetter.trim(), key);
        futureLetterEncrypted = letterResult.encrypted;
        additionalIvs.future_letter_iv = letterResult.metadata.iv;
      }
      await entityMutation(() =>
        visionsApi.create({
          title_encrypted,
          description_encrypted: descEncrypted,
          future_letter_encrypted: futureLetterEncrypted,
          encryption_metadata: mergeEncryptionMetadata(descIv, title_iv, additionalIvs),
        }),
      );
      router.back();
    } catch {
      showAlert('Error', 'Failed to create vision.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareView style={styles.container}>
      <ScreenHeader
        title="New Vision"
        left={{
          label: 'Cancel',
          onPress: () => router.back(),
          testID: 'vision-create.cancel-button',
        }}
        right={{
          label: 'Save vision',
          text: 'Save',
          tone: 'primary',
          onPress: handleSubmit,
          disabled: isSubmitting || !title.trim(),
          loading: isSubmitting,
          testID: 'vision-create.save-button',
        }}
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} keyboardShouldPersistTaps="handled">
        {/* Title */}
        <Text style={styles.label}>TITLE *</Text>
        <TextInput
          style={styles.input}
          value={title}
          onChangeText={setTitle}
          placeholder="Your long-term aspiration..."
          placeholderTextColor={colors.placeholder}
          autoFocus
          accessibilityLabel="Vision title"
          testID="vision-create.title-input"
        />

        {/* Description */}
        <Text style={styles.label}>DESCRIPTION</Text>
        <TextInput
          style={styles.textArea}
          value={description}
          onChangeText={setDescription}
          placeholder="What does this vision mean to you? How does it inspire your goals?"
          placeholderTextColor={colors.placeholder}
          multiline
          textAlignVertical="top"
          accessibilityLabel="Vision description"
          testID="vision-create.description-input"
        />

        {/* Future Letter */}
        <Text style={styles.label}>LETTER FROM YOUR FUTURE SELF</Text>
        <TextInput
          style={styles.textArea}
          value={futureLetter}
          onChangeText={setFutureLetter}
          placeholder="Write as if you've already achieved this vision. What does your life look like? How does it feel? What would you tell your past self?"
          placeholderTextColor={colors.placeholder}
          multiline
          textAlignVertical="top"
          accessibilityLabel="Future letter"
          testID="vision-create.future-letter-input"
        />

        <View style={styles.tipBox}>
          <Text style={styles.tipTitle}>{'\u{1F4A1}'} Tips</Text>
          <Text style={styles.tipText}>
            Visions are your long-term aspirations that guide your goals and tasks.
            Think about what you want to achieve in the next 3-5 years. The future letter helps you visualize success vividly.
          </Text>
        </View>

        {/* AI Suggest Button */}
        {llm.available === false && (
          <AiUnavailableBanner reason={llm.unavailableReason} />
        )}
        {llm.available === true && (
          <TouchableOpacity
            style={styles.aiButton}
            onPress={handleAiSuggest}
            disabled={llm.loading}
            accessibilityLabel="Get AI suggestions"
            accessibilityRole="button"
            testID="vision-create.ai-suggest-button"
          >
            <Text style={styles.aiButtonText} accessible={false}>{'\u{1F9E0}'} AI Suggest</Text>
          </TouchableOpacity>
        )}

        {showAiResults && (
          <AiSuggestionsCard
            loading={llm.loading}
            modelDownloading={llm.modelDownloading}
            suggestions={aiSuggestions}
            error={llm.error}
            onApplySuggestion={(text) => {
              setDescription(text);
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
            onDismiss={() => {
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
          />
        )}
      </ScrollView>
    </KeyboardAwareView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  label: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 20 },
  input: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 16, color: theme.colors.textPrimary },
  textArea: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 15, color: theme.colors.textPrimary, minHeight: 150 },
  tipBox: { backgroundColor: theme.colors.primaryBg, borderRadius: 10, padding: 14, marginTop: 16 },
  tipTitle: { fontSize: 14, fontWeight: '700' as const, color: theme.colors.primaryDark, marginBottom: 4 },
  tipText: { fontSize: 13, color: theme.colors.primary, lineHeight: 18 },
  aiButton: { backgroundColor: theme.colors.aiBg, borderWidth: 1, borderColor: theme.colors.aiBorder, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const, marginTop: 16 },
  aiButtonText: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.ai },
});
